import mapValues from 'lodash/mapValues'
import Payment from 'payment'
import D from 'date-fp'
import { equals, either, isEmpty, isNil, trim } from 'ramda'

let validators = {
  required: {
    message: 'This is a required field',
    validator: value => value,
  },
  name: {
    message: 'Invalid name',
    validator: value => value.match(/^[a-zA-Z\s-.']+$/),
  },
  cityName: {
    message: 'Invalid name',
    validator: value => value.match(/^[a-zA-Z\s-.']+$/),
  },
  number: {
    message: 'Must be a number',
    validator: value => value && !isNaN(Number(value)),
  },
  hour: {
    message: 'Enter valid hour ending with am or pm',
    validator: (value = '') =>
      value === '' || /^(\d+)\s*(pm|am)\s*$/gm.test(value),
  },
  nonNegative: {
    message: 'Must be greater than or equal to 0',
    validator: value => value && Number(value) >= 0,
  },
  nonNegativeCurrency: {
    message: 'Must be $0 or greater',
    validator: value => value && Number(value) >= 0,
  },
  zipcode: {
    message: 'Must be a valid zipcode',
    validator: value => value && value.length === 5 && !isNaN(value),
  },
  nonEmpty: {
    message: 'Must not be empty',
    validator: value => {
      return typeof value === 'string'
        ? !either(isEmpty, isNil)(trim(value))
        : !either(isEmpty, isNil)(value)
    },
  },
  oneUpper: {
    message: 'At least one uppercase character',
    validator: value => value.match(/[A-Z]/),
  },
  oneLower: {
    message: 'At least one lowercase character',
    validator: value => value.match(/[a-z]/),
  },
  oneNumber: {
    message: 'At least one number',
    validator: value => value.match(/[0-9]/),
  },
  oneSpecial: {
    message:
      'At least one character from (! @ # $ % ^ & * ? + = / \\ | - ~ . , > <)',
    // this essentially validates the whole string so that we can
    // guarantee that only the special characters we allow can be
    // included. Otherwise, the regex would totally validate a case
    // where other special characters could be included, as long
    // as the ones we allowed were present. That's not what we want.
    // Note: we do not check length here, just characters
    validator: value =>
      value.match(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*?+=/\\|\-~.,><])[A-Za-z\d!@#$%^&*?+=/\\|\-~.,><]+$/,
      ),
  },
  email: {
    message: 'Please enter a valid email address',
    validator: value =>
      // eslint-disable-next-line
      !value ||
      (value &&
        /^(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/i.test(
          value,
        )),
  },
  min: {
    message: length => `At least ${length} characters`,
    validator: (value, length) => value.length >= length,
  },
  max: {
    message: length => `Comments must be ${length} characters or less`,
    validator: (value, length) => value.length <= length,
  },
  length: {
    message: length => `Must be ${length} characters`,
    validator: (value, length) => value.length === Number(length),
  },
  creditCard: {
    message: 'Invalid card number',
    validator: Payment.fns.validateCardNumber,
  },
  expirationDate: {
    message: 'Invalid expiration date',
    validator: Payment.fns.validateCardExpiry,
  },
  cvv: {
    message: 'Invalid CVV',
    validator: Payment.fns.validateCardCVC,
  },
  dateFormat: {
    message: 'Please format dates as MM/DD/YYYY',
    validator: value => /^[\d]{2}\/[\d]{2}\/[\d]{4}$/.test(value),
  },
  noUnderAge: {
    message: 'You must be at least 18 years old to create an account',
    validator: value => D.add('years', 18, new Date(value)) <= new Date(),
  },
  noOverAge: {
    message: 'You must be under 65 to be covered',
    validator: value => D.add('years', 65, new Date(value)) >= new Date(),
  },
  notFarPast: {
    message: 'You must be under 125 to create an account',
    validator: value => D.add('years', 125, new Date(value)) >= new Date(),
  },
  future: {
    message: 'Must be in the future',
    validator: value => D.diff('minutes', new Date(), new Date(value)) > 0,
  },
  futureTwoWeeks: {
    message: 'Must be at least two weeks in the future',
    validator: value => D.diff('days', new Date(), new Date(value)) > 14,
  },
  mustBeTrue: {
    message: 'Must be selected',
    validator: equals(true),
  },
  notFuture: {
    message: `Date cannot be in the future`,
    validator: value => D.diff('days', new Date(), new Date(value)) < 0,
  },
  // valid date must come after the other date checks or else their
  // clever validation messages won't be seen, only 'Invalid date'.
  validDate: {
    message: 'Please format dates as MM/DD/YYYY',
    validator: value => {
      const isDateOfBirth =
        /(\d{4})-(\d{2})-(\d{2})(T\d{2}:\d{2}:\d{2}\.\d{3}Z)?/gm.test(value) //either YYYY-MM-DD or ISO String, which is what is coming in after the formatter

      if (isDateOfBirth) return true

      const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]
      const validationRegex =
        /^(0[1-9]|1[0-2])\/(0[1-9]|1\d|2\d|3[01])\/(19|20)\d{2}$/
      if (validationRegex.test(value)) {
        const [month, day] = value.split('/')
        const maxDayInMonth = daysInMonth[Number(month) - 1] || 0
        return Number(month) <= 12 && Number(day) <= maxDayInMonth
      } else {
        return false
      }
    },
  },
  ssn: {
    message: 'Invalid format',
    validator: value => /^[\d]{3}-[\d]{2}-[\d]{4}$/.test(value),
  },
  phone: {
    message: 'Must be 10 digits in length',
    validator: value => /^\d{3}-?\d{3}-?\d{4}$/.test(value),
  },
  amount: {
    message: 'Invalid amount',
    validator: value =>
      !/[.]$/.test(value) && // make sure the number doesn't end with a decimal point
      !/[.]\d$/.test(value) && // make sure there isnt only one digit after decimal
      !/[.]\d{2}\d+$/.test(value), // make sure there isnt more than 2 digits after decimal
  },
  contributionPercentage: {
    message: 'Contribution must be between 0.01 - 100.00%',
    validator: value =>
      !isNaN(value) && Number(value) >= 0.01 && Number(value) <= 100,
  },
  contributionDollarAmount: {
    message: 'Contribution must be $0.01 or more',
    validator: value => !isNaN(value) && Number(value) >= 0.01,
  },
}

const createValidators = validators =>
  mapValues(validators, val => {
    return options => {
      const message =
        typeof val.message === 'function' ? val.message(options) : val.message
      const validator = value =>
        val.validator(value, options) ? undefined : message
      validator.label = message
      return validator
    }
  })

validators = createValidators(validators)

export default validators



// WEBPACK FOOTER //
// ./src/sidecar-utils/lib/enhancers/toReduxFormField/validation.js